import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { hero, contactInfo, marqueeTags, founder } from "../data/mock";
import { Button } from "./ui/button";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine, scrollToId, EASE } from "../lib/motion";

export const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col bg-[#FAF7F2] overflow-hidden grain">
      {/* Decorative parallax orbs */}
      <motion.div style={{ y: yOrb }} className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-brand-400/15 blur-3xl pointer-events-none" />
      <motion.div style={{ y: yOrb }} className="absolute top-1/2 -left-48 w-[420px] h-[420px] rounded-full bg-[#A86532]/10 blur-3xl pointer-events-none" />
      <div className="absolute top-24 right-[8%] hidden lg:block pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
          className="w-64 h-64 rounded-full border border-brand-600/20"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 1.1, ease: EASE }}
          className="w-40 h-40 rounded-full border border-[#A86532]/25 -mt-44 ml-36"
        />
      </div>

      <motion.div style={{ y: yText }} className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-16">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-600/30 bg-brand-50/60 text-brand-700 text-xs font-semibold uppercase tracking-[0.18em] mb-8"
            data-testid="hero-badge"
          >
            {hero.badge}
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display font-semibold text-stone-900 tracking-tight leading-[1.04] text-[2.6rem] sm:text-6xl lg:text-[4.6rem]">
            <MaskedLine delay={0.25}>Make your business</MaskedLine>
            <MaskedLine delay={0.38}>
              <em className="text-brand-700 not-italic font-display ">easier to run</em> and
            </MaskedLine>
            <MaskedLine delay={0.51}>
              better equipped to <em className="text-[#A86532] font-display ">grow</em>.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl"
            data-testid="hero-subheadline"
          >
            {hero.subheadline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-4 text-sm sm:text-base text-stone-500  font-display"
          >
            {hero.extra}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              onClick={() => window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer")}
              data-testid="hero-primary-cta"
              className="bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-13 px-8 py-6 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
            >
              {hero.primaryCta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button
              onClick={() => scrollToId("recognition")}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 text-stone-700 hover:text-brand-700 font-medium text-base link-underline"
            >
              {hero.secondaryCta}
              <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-12 flex items-center gap-4"
            data-testid="hero-founder-line"
          >
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-brand-600/30">
              <img src={founder.photo} alt="Shakirah Akinwale" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-900">{hero.founderName}</p>
              <p className="text-xs text-stone-500">{hero.founderTitle}</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Editorial marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative z-10 border-t border-stone-900/10 py-5 overflow-hidden"
        data-testid="hero-marquee"
      >
        <div className="marquee-track">
          {[...marqueeTags, ...marqueeTags].map((tag, i) => (
            <span key={i} className="flex items-center shrink-0">
              <span className="text-sm font-display  text-stone-500 whitespace-nowrap">{tag}</span>
              <span className="mx-6 text-brand-500">✳</span>
            </span>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
